import { useState, useContext, useEffect } from "react";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { Button } from "@/modern/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/modern/components/ui/dialog";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { Eye, EyeOff, Trash2 } from "lucide-react";
import { useBoardPermissions } from "../hooks/useBoardPermissions";

interface OpenAIKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OpenAIKeyModal = ({ isOpen, onClose }: OpenAIKeyModalProps) => {
  const { project } = useContext(BryntumContext);
  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasExistingKey, setHasExistingKey] = useState(false);

  // Use shared permission hook
  const { canWrite } = useBoardPermissions();

  // Load current key value when modal opens
  useEffect(() => {
    if (isOpen) {
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      const currentKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.openAiApiKey, null, true);
      const hasKey = Boolean(currentKey);
      setHasExistingKey(hasKey);
      setApiKey(currentKey || "");
      setShowPassword(false);
    }
  }, [isOpen]);

  const handleSaveApiKey = async () => {
    if (!project) return;

    if (!canWrite) {
      toast.error("You don't have permission to update the OpenAI API key");
      return;
    }

    if (!apiKey || apiKey.trim() === "") {
      toast.error("Please enter an API key or use the Remove button to delete it");
      return;
    }

    setIsSaving(true);
    try {
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.openAiApiKey, apiKey, true);
      // Set the flag to indicate an API key exists
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "true", true);
      // @ts-expect-error - project type not fully defined
      await project.sync();

      toast.success("OpenAI API key saved successfully");
      onClose();
    } catch (error) {
      console.error("Error saving OpenAI API key:", error);
      toast.error("Failed to save OpenAI API key. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveKey = async () => {
    if (!project) return;

    if (!canWrite) {
      toast.error("You don't have permission to remove the OpenAI API key");
      return;
    }

    if (!hasExistingKey) {
      toast.error("No API key to remove");
      return;
    }

    setIsSaving(true);
    try {
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.deleteSetting(PERSISTENCE_STATE_KEYS.openAiApiKey, true);
      // Set the flag to false to indicate no API key exists
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true);
      // @ts-expect-error - project type not fully defined
      await project.sync();

      toast.success("OpenAI API key removed successfully");
      onClose();
    } catch (error) {
      console.error("Error removing OpenAI API key:", error);
      toast.error("Failed to remove OpenAI API key. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Configure OpenAI API Key</DialogTitle>
          <DialogDescription>Enter your OpenAI API key to enable AI-powered features in your board.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="input1" className="text-sm font-medium">
              OpenAI API Key
            </Label>
            <div className="relative">
              <Input
                id="input1"
                type={showPassword ? "text" : "password"}
                placeholder="sk-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isSaving}
                autoComplete="one-time-code"
                data-form-type="other"
                className="font-mono text-sm pr-10"
              />
              <Button type="button" variant="ghost" size="icon-sm" className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setShowPassword(!showPassword)} disabled={isSaving}>
                {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {hasExistingKey ? "✓ Current key is set. Enter a new key to update it, or use the Remove button below." : "No key is currently set. Enter a new key to save it."}
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
              <strong>How to get your API key:</strong>
              <br />
              1. Visit{" "}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">
                platform.openai.com/api-keys
              </a>
              <br />
              2. Sign in or create an OpenAI account
              <br />
              3. Create a new API key and copy it here
            </p>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Important:</strong> Keep your API key secure and never share it publicly. The key can only be viewed and managed by Board Owners.
          </p>
        </div>

        <DialogFooter className="sm:justify-between">
          {hasExistingKey ? (
            <>
              <Button variant="destructive" onClick={handleRemoveKey} disabled={isSaving} size="sm" className="flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Remove Key
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={isSaving} size="sm">
                  Cancel
                </Button>
                <Button variant="default" onClick={handleSaveApiKey} disabled={isSaving} size="sm">
                  {isSaving ? "Updating..." : "Update Key"}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={onClose} disabled={isSaving} size="sm">
                Cancel
              </Button>
              <Button variant="default" onClick={handleSaveApiKey} disabled={isSaving} size="sm">
                {isSaving ? "Saving..." : "Save Key"}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
